import { useEffect, useId, useRef, useState } from "preact/hooks";

import { useAnchoredPosition } from "../hooks/useAnchoredPosition";
import { useIsDesktop } from "../hooks/useIsDesktop";
import { useModalA11y } from "../hooks/useModalA11y";
import { InfoIcon } from "./InfoIcon";

export interface InfoTooltipProps {
  /** Required, never defaulted (design spec §3.4) — feeds the trigger's
   * per-instance `aria-label` ("More about X"), generated by the caller
   * from `lib/helpCopy.ts` so two triggers on one screen are never
   * indistinguishable to a screen-reader user. */
  label: string;
  /** Plain string ONLY (SA guardrail #2 / Security's E5 gate condition) —
   * never markdown, never HTML, never `dangerouslySetInnerHTML`. There is
   * no `<TooltipLink>`/interactive-content whitelist in this v1; that
   * scope is explicitly deferred pending its own SA/Security ruling. */
  content: string;
}

// design/sprint-019-info-icon-component.md §2.1 — narrower than
// `WeatherDetailOverlay`'s 340px `.heat-popover` (tooltip content is a
// sentence or two, not a detail panel). Passed to the shared
// `useAnchoredPosition` hook as a conservative upper bound for the clamp
// math — `.info-popover` itself is `width: max-content` up to this max.
const POPOVER_MAX_WIDTH = 280;
const POPOVER_MARGIN = 16;
const POPOVER_GAP = 8;

// design spec §3.1 — 300ms open / 150ms close. Long enough to filter
// incidental mouse-pass-over on a dense screen, short enough to still
// feel responsive; the close delay gives a mouse moving FROM the icon
// INTO the open tooltip body time to arrive before it vanishes.
const HOVER_OPEN_DELAY_MS = 300;
const HOVER_CLOSE_DELAY_MS = 150;

/** design spec §3.3's build note — some mobile browsers fire a synthetic
 * `mouseenter`/`mouseleave` on first tap of hover-CSS'd elements. Gating
 * every hover listener behind a real pointer-type check keeps touch
 * devices on the tap-toggle path exclusively, so a tap can never leave a
 * "hover-opened, nothing to un-hover" popover stuck open. */
function supportsHoverPointer(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia("(hover: hover) and (pointer: fine)").matches;
}

/**
 * Sprint-019 E5b — the accessible info-icon trigger + tooltip/popover
 * (design/sprint-019-info-icon-component.md, CD sign-off 2026-07-12).
 * Reachable through three independent modalities, all required, none a
 * fallback for the others (§3): hover (mouse, 300ms open / 150ms close
 * grace), focus (keyboard, immediate open, Escape closes + returns focus
 * to the trigger), and tap (touch — the only modality where this toggles,
 * gated behind `supportsHoverPointer()` so it never collides with the
 * hover path on a touch device).
 *
 * Desktop (`≥1024px`, `hooks/useIsDesktop.ts`'s shared breakpoint) renders
 * an anchored `.info-popover`, always `role="tooltip"` (SA guardrail #2 —
 * `content` is a plain string only in this v1, so there is no
 * `role="dialog"` derivation to make) positioned via the SAME shared
 * `hooks/useAnchoredPosition.ts` helper `WeatherDetailOverlay` now also
 * consumes (SA guardrail #3) — deliberately NOT wrapped in
 * `useModalA11y` (SA guardrail #1: a tooltip is not a modal, no focus
 * trap on desktop). Escape and outside-click are handled with a small,
 * local listener instead.
 *
 * Mobile (`<1024px`) renders a bottom sheet reusing `.heat-sheet` chrome
 * verbatim (design spec §2.2) — the ONE variant that IS effectively a
 * small modal (dims the backdrop, covers part of the screen), so it's the
 * ONE variant that reuses `useModalA11y`'s existing focus-trap/Escape/
 * focus-restore/hardware-Back contract, matching every other sheet in
 * this app.
 */
export function InfoTooltip({ label, content }: InfoTooltipProps) {
  const [open, setOpen] = useState(false);
  const isDesktop = useIsDesktop();
  // Non-reactive by design — unlike `useIsDesktop`'s width-driven
  // breakpoint, a device's hover/pointer capability does not change
  // mid-session in any case this app needs to handle.
  const [hoverCapable] = useState(supportsHoverPointer);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const openTimerRef = useRef<number | null>(null);
  const closeTimerRef = useRef<number | null>(null);
  // Guards the "Escape closes + returns focus to trigger" step (§3.2):
  // `HTMLElement.focus()` dispatches its `focus` event SYNCHRONOUSLY, so
  // without this flag `closeAndRestoreFocus`'s own `triggerRef.current
  // .focus()` call would immediately re-enter `handleFocus` and reopen
  // the very popover Escape just closed.
  const restoringFocusRef = useRef(false);
  const popoverId = `info-popover-${useId()}`;

  function clearTimers() {
    if (openTimerRef.current !== null) {
      window.clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }

  // Belt-and-suspenders: clear any in-flight hover timer on unmount so a
  // stray `setOpen` never fires against an unmounted component.
  useEffect(() => clearTimers, []);

  function closeNow() {
    clearTimers();
    setOpen(false);
  }

  function closeAndRestoreFocus() {
    closeNow();
    restoringFocusRef.current = true;
    triggerRef.current?.focus();
    restoringFocusRef.current = false;
  }

  function handleMouseEnter() {
    if (!hoverCapable) return;
    clearTimers();
    openTimerRef.current = window.setTimeout(() => setOpen(true), HOVER_OPEN_DELAY_MS);
  }

  function handleMouseLeave() {
    if (!hoverCapable) return;
    clearTimers();
    closeTimerRef.current = window.setTimeout(() => setOpen(false), HOVER_CLOSE_DELAY_MS);
  }

  function handleFocus() {
    if (restoringFocusRef.current) return;
    // §3.2 — keyboard focus opens immediately, no hover-style delay: a
    // deliberate discrete Tab can't "accidentally graze" the way a mouse
    // cursor can.
    clearTimers();
    setOpen(true);
  }

  function handleBlur() {
    // §3.2 — focus leaving the trigger (or, on desktop, leaving the
    // popover body too) is itself a valid dismiss signal. Deferred one
    // tick so focus moving FROM the trigger INTO the popover doesn't
    // read as "focus left the component" mid-transit.
    window.setTimeout(() => {
      const active = document.activeElement;
      const stillInside =
        (triggerRef.current && triggerRef.current.contains(active)) ||
        (containerRef.current && containerRef.current.contains(active));
      if (!stillInside) closeNow();
    }, 0);
  }

  function handleTriggerClick() {
    if (hoverCapable) return; // desktop mouse: hover + focus own the open path above
    if (!isDesktop && open) {
      closeSheet();
      return;
    }
    setOpen(true);
  }

  // Desktop-only: Escape closes + returns focus to the trigger;
  // click/tap anywhere outside the trigger AND the popover closes too.
  // No focus trap — SA guardrail #1.
  useEffect(() => {
    if (!open || !isDesktop) return undefined;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      closeAndRestoreFocus();
    }
    function onPointerDown(e: MouseEvent) {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (containerRef.current?.contains(target)) return;
      closeNow();
    }
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [open, isDesktop]);

  const popoverStyle = useAnchoredPosition({
    active: open,
    isDesktop,
    triggerRef,
    width: POPOVER_MAX_WIDTH,
    margin: POPOVER_MARGIN,
    gap: POPOVER_GAP,
  });

  // Mobile-only in practice (`active` gates on `!isDesktop`) — desktop
  // never pushes a modal-stack entry or installs a focus trap through
  // this hook, satisfying SA guardrail #1.
  const { close: closeSheet } = useModalA11y(containerRef, {
    active: open && !isDesktop,
    onClose: closeAndRestoreFocus,
  });

  return (
    <span class="info-tooltip-anchor">
      <button
        ref={triggerRef}
        type="button"
        class="info-icon-trigger"
        aria-expanded={open}
        aria-describedby={isDesktop ? popoverId : undefined}
        aria-label={label}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleFocus}
        onBlur={isDesktop ? handleBlur : undefined}
        onClick={handleTriggerClick}
      >
        <InfoIcon />
      </button>

      {open && isDesktop ? (
        <>
          <div class="popover-scrim" onClick={closeNow} />
          <div
            ref={containerRef}
            id={popoverId}
            role="tooltip"
            class="info-popover"
            style={{ ...popoverStyle }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {content}
          </div>
        </>
      ) : null}

      {open && !isDesktop ? (
        <>
          <div class="heat-sheet-backdrop" onClick={closeSheet} />
          <div
            ref={containerRef}
            class="heat-sheet"
            role="dialog"
            aria-modal="true"
            aria-label={label}
          >
            <div class="heat-sheet-handle" aria-hidden="true" />
            <div class="heat-sheet-head">
              <p class="heat-sheet-sub" style={{ margin: 0 }}>
                {content}
              </p>
              <button
                type="button"
                class="icon-btn"
                onClick={closeSheet}
                aria-label="Close"
                title="Close"
              >
                ×
              </button>
            </div>
          </div>
        </>
      ) : null}
    </span>
  );
}
